import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import {setComma} from "../../../utils/format";
import {useMemberWallet} from "../../../hooks/useMemberWallet";
import {alertAfterFunction, defaultAlert, defaultConfirm} from "../../../utils/alert";
import {Loading} from "../../../components/common/Loading";
import {usePrepaidSetting} from "../../../hooks/usePrepaidSetting";
import {ChargeRequestTypes} from "../../../types/transaction/transaction.types";
import {saveChargeData} from "../../../api/transaction/chargeApi";
import {getPaymentStatus, requestPayment, requestPaymentCancel} from "../../../services/payment/paymentService";


const ChargePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [paymentMethod, setPaymentMethod] = useState("rad1");
    const {prepaidSetting, loading} = usePrepaidSetting();
    const {memberWalletInfo, loading: walletLoading, error, refetch} = useMemberWallet();

    const [isLoading, setIsLoading] = useState(false);

    const [reqChargeAmount, setReqChargeAmount] = useState<number>(0);
    const [chargeFee, setChargeFee] = useState<number>(0);
    const [tranAmt, setTranAmt] = useState<number>(0);

    // 충전 완료 후 돌아갈 경로 (PaymentPage 잔액 부족 흐름에서 sessionStorage로 전달됨)
    const [returnPath] = useState<string | null>(() => {
        const path = sessionStorage.getItem('chargeReturnPath');
        sessionStorage.removeItem('chargeReturnPath'); // 읽은 즉시 제거 (모바일은 form.submit 직전 재등록)
        return path;
    });

    useEffect(() => {
        const state = location.state as { reqChargeAmount?: number } | null;
        if (state?.reqChargeAmount && prepaidSetting) {
            updateChargeAmount(state.reqChargeAmount);
        }
    }, [prepaidSetting]);


    const calculateFee = (amount: number): number => {
        if (!prepaidSetting) return 0;

        const isUnder10k = amount <= 10000;
        const feeValue = isUnder10k ? prepaidSetting.chargeFeeUnderValue : prepaidSetting.chargeFeeOverValue;
        const feeUnit = isUnder10k ? prepaidSetting.chargeFeeUnderUnit : prepaidSetting.chargeFeeOverUnit;

        return feeUnit === "WON" ? feeValue : Math.floor((amount * feeValue) / 100);
    };

    const updateChargeAmount = (amount: number) => {
        if (chargeAmountValidation(amount)) {
            setReqChargeAmount(0);
            setChargeFee(0);
            setTranAmt(0);
            return;
        }

        const fee = calculateFee(amount);
        setReqChargeAmount(amount);
        setChargeFee(fee);
        setTranAmt(amount + fee);
    };

    const handleAmountClick = (value: number) => {
        updateChargeAmount(reqChargeAmount + value);
    };

    const inputAmountValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const amount = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
        updateChargeAmount(amount);
    };

    const chargeAmountValidation = (value: number) => {
        const chargeAmount = Number(value);
        const maxLimitBalance = Number(memberWalletInfo?.isKyc ? prepaidSetting?.namedMaxBalance : prepaidSetting?.anonMaxBalance);
        const chargeMaxLimitAmount = Number(memberWalletInfo?.isKyc ? prepaidSetting?.namedMaxChargeOnce : prepaidSetting?.anonMaxChargeOnce);
        const currentBalance = Number(memberWalletInfo?.prepaidBalance || 0);
        const chargePossibleAmount = Math.max(0, maxLimitBalance - currentBalance);

        if (chargePossibleAmount < chargeAmount) {
            defaultAlert("충전 가능 금액은 " + setComma(chargePossibleAmount) + "원 입니다.");
            return true;
        }
        if (maxLimitBalance < chargeAmount) {
            defaultAlert("충전 보유 가능 금액은 " + setComma(maxLimitBalance) + "원 입니다.");
            return true;
        }
        if (chargeMaxLimitAmount < chargeAmount) {
            defaultAlert("1회 최대 충전 가능 금액은 " + setComma(chargeMaxLimitAmount) + "원 입니다.");
            return true;
        }
        return false;
    };

    const handleAmountReset = () => {
        setChargeFee(0);
        setTranAmt(0);
        setReqChargeAmount(0);
    };


    const handleChargeReq = async () => {
        if (reqChargeAmount == 0) {
            defaultAlert("충전금액을 입력해주세요.");
            return;
        }
        const chargeMinLimitAmount = Number(prepaidSetting?.anonMinChargeOnce)
        if (reqChargeAmount < chargeMinLimitAmount) {
            defaultAlert("최소 충전 금액은 " + setComma(chargeMinLimitAmount) + "원 입니다.");
            return;
        }

        //충전요청 pg 호출
        try {
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            // 1. 로그인하여 토큰 받기

            if (!memberWalletInfo) return;

            const paymentResponse = await requestPayment(
                tranAmt, chargeFee, "WINPAY", memberWalletInfo.memberName, memberWalletInfo.memberId, isMobile);
            if (paymentResponse?.data.success) {
                try {
                    const urlData = JSON.parse(paymentResponse.data.paymentUrl);
                    if (!urlData) return;
                    if (isMobile) {
                        openBankPayWalletIsMobile(urlData);
                    } else {
                        openBankPayWallet(urlData);
                    }
                } catch (parseErr) {
                    console.error('Failed to parse payment URL:', parseErr);
                    defaultAlert('결제 정보 처리에 실패했습니다.');
                    return;
                }
            }
        } catch (err) {
            console.error(err);
            defaultAlert('결제 요청이 실패했습니다.');
        }
    }


    //pc 방식
    const openBankPayWallet = (urlData: any) => {
        setIsLoading(true);

        if (!urlData) return;

        const width = 500;
        const height = 600;

        const popup = window.open('', 'BankPayPopup',
            `width=${width}, height=${height} ,left=0,top=0`);

        if (!popup || popup.closed || typeof popup.closed == 'undefined') {
            setIsLoading(false);
            defaultAlert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
            return;
        }

        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('action', urlData.url);
        form.setAttribute('target', 'BankPayPopup');

        for (const key in urlData) {
            if (key !== 'url') {
                const input = document.createElement('input');
                input.setAttribute('type', 'hidden');
                input.setAttribute('name', key);
                input.setAttribute('value', urlData[key]);
                form.appendChild(input);
            }
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);

        // 결제창 닫힘 감지
        const checkPayment = setInterval(() => {
            if (popup.closed) {
                setIsLoading(false);
                clearInterval(checkPayment);
                checkPaymentResult();
            }
        }, 1000);
    }

    //폼 서브밋 방식 (모바일 버전)
    const openBankPayWalletIsMobile = (urlData: any) => {
        setIsLoading(true);

        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('action', urlData.url);

        for (const key in urlData) {
            if (key !== 'url') {
                const input = document.createElement('input');
                input.setAttribute('type', 'hidden');
                input.setAttribute('name', key);
                input.setAttribute('value', urlData[key]);
                form.appendChild(input);
            }
        }
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
            const input = document.createElement('input');
            input.setAttribute('type', 'hidden');
            input.setAttribute('name', 'app_scheme');
            input.setAttribute('value', "autocare://");
            form.appendChild(input);
        }
        sessionStorage.setItem("reqChargeAmount", reqChargeAmount.toString());
        sessionStorage.setItem("chargeFee", chargeFee.toString());
        sessionStorage.setItem("tranAmt", tranAmt.toString());
        if (returnPath) {
            sessionStorage.setItem("chargeReturnPath", returnPath);
        } else {
            sessionStorage.removeItem("chargeReturnPath");
        }

        // iOS 네이티브 닫기 버튼용 전역 취소 함수 등록
        // form.submit() 이후 React 컴포넌트가 언마운트되므로 submit 직전에 등록
        (window as any).__cancelBankPay = async () => {
            const tid = sessionStorage.getItem("tid");
            if (tid) {
                try {
                    await requestPaymentCancel(tid, "사용자 취소");
                } catch (e) {
                    console.error("결제 취소 실패", e);
                } finally {
                    sessionStorage.removeItem("tid");
                    sessionStorage.removeItem("reqChargeAmount");
                    sessionStorage.removeItem("chargeFee");
                    sessionStorage.removeItem("tranAmt");
                    delete (window as any).__cancelBankPay;
                }
            }
            const cancelReturnPath = sessionStorage.getItem("chargeReturnPath");
            sessionStorage.removeItem("chargeReturnPath");
            window.location.href = cancelReturnPath || '/wallet/charge';
        };

        document.body.appendChild(form);
        form.submit();
    }

    const checkPaymentResult = async () => {
        setIsLoading(true);

        const tid = sessionStorage.getItem("tid")
        const token = sessionStorage.getItem("payToken")

        if (!token) {
            setIsLoading(false);
            defaultAlert("결제 도중 연결이 끊겼습니다.\n다시 시도해주세요.");
            return;
        }

        let isSuccess = false;
        for (let i = 0; i < 5; i++) {
            const response = await getPaymentStatus(tid || "");
            if (response.data.success) {
                await saveChargeRequest(response.data);
                isSuccess = true;
                break;
            }
            if (i < 4) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
        if (!isSuccess) {
            defaultAlert("결제 확인에 실패했습니다. 고객센터에 문의하세요.");
        }
        setIsLoading(false);
    };



    const saveChargeRequest = async (paymentData: any) => {
        const tid = sessionStorage.getItem("tid");
        try {
            const chargeRequest: ChargeRequestTypes = {
                chargeAmount: reqChargeAmount,           // 충전 요청 금액
                chargeFee: chargeFee,                    // 충전 수수료
                paymentAmount: paymentData.amt || tranAmt, // pg 결제 금액
                paymentTransactionId: paymentData.tid,   // 결제거래ID
                paymentCompany: paymentData.van || 'BANKPAY'    // 결제회사
            };

            // 백엔드 API 호출
            const response = await saveChargeData(chargeRequest);

            if (response.data.code === "00") {
                alertAfterFunction("충전이 완료되었습니다.", () => {
                    navigate(returnPath || '/wallet/charge-list');
                });
            } else {
                await requestPaymentCancel(tid || "", "결제 에러");
            }

        } catch (error) {
            await requestPaymentCancel(tid || "", "결제 에러");
            console.error('충전 요청 실패:', error);
            defaultAlert("충전 요청 실패");
        } finally {
            sessionStorage.removeItem("tid");
            sessionStorage.removeItem("reqChargeAmount");
            sessionStorage.removeItem("chargeFee");
            sessionStorage.removeItem("tranAmt");
        }
    };



    const chargePossibleAmount = memberWalletInfo && prepaidSetting
        ? (Number(memberWalletInfo.isKyc ? prepaidSetting.namedMaxBalance : prepaidSetting.anonMaxBalance) - Number(memberWalletInfo.prepaidBalance)) || 0
        : 0;

    return (
        <>
            <Loading
                loading={isLoading}
                onClose={() => setIsLoading(false)}
            />
            {!isLoading && memberWalletInfo && prepaidSetting && (
                <div>
                    <div className="bottom-btn">
                        <button type="button" className="btn confirm" onClick={handleChargeReq}>
                            충전하기
                        </button>
                    </div>

                    <main className="container type_gray">
                        <form className="form-box">
                            <section className="bg-white">
                                <fieldset>
                                    <legend>충전금액</legend>
                                </fieldset>
                                <div className="inner-box">
                                    <div className="form-item">
                                        <label htmlFor="amount" className="input-tit big">
                                            충전금액
                                            <span className="guide float-right">
                        충전가능금액 : <strong>{setComma(chargePossibleAmount < 0 ? 0 : chargePossibleAmount)}원</strong>
                      </span>
                                        </label>
                                        <div className="inputboxWrap focus">
                                            <input
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                type="text"
                                                id="amount"
                                                name="amount"
                                                className="inputbox"
                                                placeholder="0"
                                                onFocus={handleAmountReset}
                                                value={setComma(reqChargeAmount)}
                                                onChange={inputAmountValue}
                                            />
                                            <label className="unit">원</label>
                                        </div>
                                        <div className="small-btn-row">
                                            <button
                                                type="button"
                                                className="unit-btn"
                                                onClick={() => handleAmountClick(10000)}
                                            >
                                                1만원
                                            </button>
                                            <button
                                                type="button"
                                                className="unit-btn"
                                                onClick={() => handleAmountClick(50000)}
                                            >
                                                5만원
                                            </button>
                                            <button
                                                type="button"
                                                className="unit-btn"
                                                onClick={() => handleAmountClick(100000)}
                                            >
                                                10만원
                                            </button>
                                            <button
                                                type="button"
                                                className="unit-btn"
                                                onClick={() => handleAmountClick(500000)}
                                            >
                                                50만원
                                            </button>
                                            <button
                                                type="button"
                                                className="unit-btn"
                                                onClick={handleAmountReset}
                                            >
                                                초기화
                                            </button>
                                        </div>
                                        <p className="guide_txt mt10">
                                            회원님의 현재 잔액은<span
                                            className="strong"> {setComma(memberWalletInfo.prepaidBalance || "") || 0} 원</span> 입니다.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <section className="bg-white">
                                <fieldset>
                                    <legend>결제수단</legend>
                                </fieldset>
                                <div className="inner-box">
                                    <div className="form-item">
                                        <label htmlFor="" className="input-tit big">
                                            결제수단
                                        </label>
                                        <span className="circle-radio-box">
                      <input
                          type="radio"
                          id="rad1"
                          name="paymentMethod"
                          className="blind"
                          checked={paymentMethod === "rad1"}
                      />
                      <label htmlFor="rad1">실시간 계좌이체</label>
                    </span>
                                    </div>
                                </div>
                            </section>

                            <section className="bg-white">
                                <fieldset>
                                    <legend>결제정보</legend>
                                </fieldset>
                                <div className="inner-box">
                                    <div className="form-item">
                                        <label htmlFor="" className="input-tit big">
                                            결제정보
                                        </label>
                                        <div className="info-row">
                                            <span className="label">충전 금액</span>
                                            <span className="cont">{setComma(reqChargeAmount)}원</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">충전 수수료</span>
                                            <span
                                                className="cont">{setComma(chargeFee)}원</span>
                                        </div>
                                        <div className="info-row top-line">
                                            <span className="label"><strong>총 결제금액</strong></span>
                                            <span className="cont">
                                                <strong>{setComma(tranAmt)}원</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </form>
                    </main>
                </div>
            )}
        </>
    );
};

export default ChargePage;
